// OpenerHost — resolves preset by id and renders it with merged settings.
// Used in BOTH the public invitation overlay AND the Studio preview, so
// behavior is identical across environments.
//
// Phase 1.9.18 — preset contract gained two OPTIONAL props:
//   • autoStart  — if false, the preset should mount its first frame but
//                  NOT kick the animation timeline. The host invokes the
//                  imperative entry point below to start the timeline.
//   • startRef   — a ref the preset assigns a start() function to. The
//                  host calls startRef.current?.() to begin the animation
//                  once the guest has tapped the opening.
// Existing presets that ignore these props continue to auto-start exactly
// as they do today (back-compat is built into the defaults).

import React, { useMemo } from 'react';
import { getPreset } from './openerRegistry';
import { migrateOpenerSettings } from './migrations';

/**
 * @param {Object} props
 * @param {string} props.presetId
 * @param {number} [props.version]
 * @param {Object} [props.settings]
 * @param {boolean} [props.reducedMotion]
 * @param {boolean} [props.autoStart]              defaults to true
 * @param {{ current: (() => void) | null }} [props.startRef]
 * @param {() => void} [props.onComplete]
 */
export default function OpenerHost({
  presetId,
  version,
  settings,
  reducedMotion,
  autoStart = true,
  startRef,
  onComplete,
}) {
  const preset = getPreset(presetId);
  const merged = useMemo(() => {
    if (!preset) return null;
    const migrated = migrateOpenerSettings(presetId, version ?? preset.version, settings || {});
    return { ...preset.defaults, ...migrated };
  }, [preset, presetId, version, settings]);

  if (!preset || !merged) return null;
  const { Component } = preset;
  return (
    <Component
      settings={merged}
      reducedMotion={!!reducedMotion}
      autoStart={autoStart}
      startRef={startRef}
      onComplete={onComplete}
    />
  );
}
